import DraftRecoveryChoices from "../Components/DraftRecoveryChoices";
import useRecoverableAutosave from "../hooks/useRecoverableAutosave";
import DraftSaveStatus from "../Components/DraftSaveStatus";
import Page from "../Components/Page";
import FormTextField from "../Components/FormInputElements/FormTextField";
import FormSelectField from "../Components/FormInputElements/FormSelectField";
import { Alert, Button } from "@mui/material";
import MainButton from "../Components/Buttons/MainButton";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { formDraftKey, readFormDraftAsync, newEditorDraftKey } from "../utils/formDrafts";
import { useSelectedAggregatorGrower } from "../Components/AggregatorSelectedGrowerPanel";
import { useAuth } from "../Components/Auth/useAuth";
import WarningMessageModal from "../Components/WarningMessageModal";
import {
  axiosInstance,
  buildLegacyHeaders,
  fetchFarm,
} from "../Components/axiosFetchers";
import VersionHistoryControl, {
  HistoryViewingBanner,
} from "../Components/VersionHistoryControl";
import { FARM_CREATOR } from "../Assets/contextFactories_V0";
import useVersionHistory from "../hooks/useVersionHistory";
import { useReferenceData } from "../hooks/useReferenceData";
import { buildSparsePayload } from "../utils/sparsePayload";
import { hasComparableFormChanges } from "../utils/formComparison";
import { trackEvent } from "../analytics/ga4.js";
import { duplicateNameMessage, isConflictError } from "../utils/apiErrors.js";

const EMPTY_FARM_ERRORS = {
  name: false,
  province: false,
};

function unwrapList(payload) {
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
}

function normalizeFarmFormState(farm) {
  return {
    ...FARM_CREATOR(),
    ...(farm || {}),
    project_id: String(farm?.project_id ?? farm?.project?.id ?? ""),
  };
}

function buildFarmPayload(farm, user) {
  return buildSparsePayload({
    name: farm?.name,
    province: farm?.province,
    project_id: farm?.project_id === "" ? null : Number(farm?.project_id),
    owner_id: user?.email,
  });
}

export default function FarmFormPage() {
  const { user, impersonatedEmail } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  const selectedGrower = useSelectedAggregatorGrower();
  const { farmId } = useParams();
  const isEditing = Boolean(farmId);
  const baseIdentityKey = formDraftKey({ user, impersonatedEmail, growerPublicId: selectedGrower?.grower_profile_public_id }, `farm:${farmId || "new"}`);
  const identityKey = useMemo(() => isEditing ? baseIdentityKey : newEditorDraftKey(baseIdentityKey), [baseIdentityKey, isEditing]);
  const [recoverableDraft, setRecoverableDraft] = useState(null);
  const [errorFields, setErrorFields] = useState(EMPTY_FARM_ERRORS);
  const [farm, setFarm] = useState(FARM_CREATOR());
  const [savedFarm, setSavedFarm] = useState(FARM_CREATOR());
  const [existingFarms, setExistingFarms] = useState([]);
  const [existingProjects, setExistingProjects] = useState([]);
  const [warningOpen, setWarningOpen] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { referenceData } = useReferenceData(["provinces"]);
  const history = useVersionHistory({
    user,
    enabled: isEditing && Boolean(farmId),
    historyPath: farmId ? `/farms/${farmId}` : "",
    currentData: farm,
    normalizeDetail: normalizeFarmFormState,
    onRestore: async (snapshot) => {
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/farms/${farmId}`,
        buildSparsePayload({
          name: snapshot.name,
          province: snapshot.province,
          project_id: snapshot.project_id === "" ? null : Number(snapshot.project_id),
        }),
        { headers: buildLegacyHeaders(user) }
      );

      return normalizeFarmFormState(response.data);
    },
    onRestoreSuccess: (savedFarm) => {
      setFarm(savedFarm);
      setSavedFarm(savedFarm);
    },
  });
  const displayedFarm = history.versionData || farm;
  const isViewingHistoricalVersion = history.isViewingHistoricalVersion;

  document.title = `${isEditing ? "Edit" : "Add"} Farm Page - Field To Market Canada`;

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setErrorFields(EMPTY_FARM_ERRORS);
    setSaveError("");

    async function startupPage() {
      const [farmsResponse, projectsResponse, nextFarm] = await Promise.all([
        axiosInstance.get(process.env.REACT_APP_API_URL + "/farms?max=100", {
          headers: buildLegacyHeaders(userRef.current),
        }),
        axiosInstance.get(process.env.REACT_APP_API_URL + "/projects", {
          headers: buildLegacyHeaders(userRef.current),
        }),
        isEditing ? fetchFarm(farmId, userRef.current) : Promise.resolve(FARM_CREATOR()),
      ]);

      if (!isMounted) {
        return;
      }

      setExistingFarms(unwrapList(farmsResponse.data));
      const activeProjects = unwrapList(projectsResponse.data);
      setExistingProjects(nextFarm?.project && !activeProjects.some((project) => project.id === nextFarm.project.id)
        ? [...activeProjects, { ...nextFarm.project, name: `${nextFarm.project.name} (not currently active)` }]
        : activeProjects);
      const normalizedFarm = normalizeFarmFormState(nextFarm);
      const localRecovery = await readFormDraftAsync(identityKey);
      if (!isMounted) return;
      setRecoverableDraft(localRecovery);
      setFarm(normalizedFarm);
      setSavedFarm(normalizedFarm);
    }

    startupPage().catch(() => {
      if (isMounted) setSaveError("We couldn't load the farm form. Reload the page to try again.");
    }).finally(() => {
      if (isMounted) setIsLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [farmId, isEditing, identityKey]);

  function handleStateChange(target, key) {
    setFarm((prevState) => ({ ...prevState, [key]: target }));
    setErrorFields((current) => ({ ...current, [key]: false }));
    setSaveError("");
  }

  const isSaveDisabled = useMemo(
    () =>
      !hasComparableFormChanges(
        buildFarmPayload(farm, user),
        buildFarmPayload(savedFarm, user)
      ),
    [farm, savedFarm, user]
  );

  const autosave = useRecoverableAutosave({ draftKey: identityKey, value: farm,
    dirty: hasComparableFormChanges(farm, savedFarm), versionId: savedFarm.current_version_id,
    target: { kind: "farm", farm_id: null, field_id: null, resource_id: farmId ? Number(farmId) : farm.id || null },
    enabled: !isLoading && !isViewingHistoricalVersion, suspendSync: Boolean(recoverableDraft),
    onRecover: setRecoverableDraft });

  function hasDuplicateFarmName() {
    const normalizedName = farm.name.trim().toLowerCase();

    if (!normalizedName) {
      return false;
    }

    return existingFarms.some((existingFarm) => {
      if (!existingFarm?.name) {
        return false;
      }

      if (isEditing && String(existingFarm.id) === String(farmId)) {
        return false;
      }

      return existingFarm.name.trim().toLowerCase() === normalizedName;
    });
  }

  function isInputValid() {
    const errors = { name: !farm.name?.trim(), province: !farm.province?.trim() };
    setErrorFields(errors);
    return !Object.values(errors).some(Boolean);
  }

  async function handleSaveFarm() {
    if (hasDuplicateFarmName()) {
      setWarningOpen(true);
      return;
    }

    if (!isInputValid()) {
      return;
    }

    const payload = buildFarmPayload(farm, user);

    try {
      if (isEditing) {
        const response = await autosave.commit(payload);
        if (response.hasNewerEdits) { setSavedFarm(normalizeFarmFormState(response.data)); return; }
        trackEvent("farm_saved", {
          workflow_area: "farm",
          is_new_record: false,
          result: "success",
        });
        navigate(`/farm/${farm.id}`);
        return;
      }

      const response = await autosave.commit(payload);
      if (response.hasNewerEdits) { setSavedFarm(normalizeFarmFormState(response.data)); setFarm(current => ({ ...current, id: response.data.id })); setSaveError("Farm saved. Newer edits are still in your recoverable draft; reopen the saved farm to continue."); return; }
      trackEvent("farm_saved", {
        workflow_area: "farm",
        is_new_record: true,
        result: "success",
      });
      navigate(`/farm/${response.data.id}/field/form`);
    } catch (error) {
      console.error(error);
      if (!error.response && error.message) { setSaveError(error.message); return; }
      if (error.response?.status === 409 && typeof error.response.data?.detail === "string" && /draft|record changed|assessment changed|context changed/i.test(error.response.data.detail)) { setSaveError(error.response.data.detail); return; }
      if (isConflictError(error)) {
        setWarningOpen(true);
        return;
      }
      setSaveError(
        duplicateNameMessage(error, {
          resourceLabel: "farm",
          name: farm.name,
          fallback: "We couldn't save this farm.",
        })
      );
    }
  }

  var projectValuesArray = [
    {
      label: "None",
      value: "",
    },
    ...existingProjects.map((project) => {
      const partnerName = project.partner?.name;

      return {
        label: partnerName
          ? `${project.name} (${partnerName})`
          : project.name,
        value: String(project.id),
      };
    }),
  ];
  if (displayedFarm?.project_id && !projectValuesArray.some((project) => project.value === String(displayedFarm.project_id))) {
    projectValuesArray.push({ value: String(displayedFarm.project_id), label: "Previously assigned project (not currently active)" });
  }

  if (isLoading) return <Page title={isEditing ? "Edit Farm" : "New Farm"}><p role="status">Loading farm form…</p></Page>;

  return (
    <Page
      title={isEditing ? "Edit Farm" : "New Farm"}
      headerBorderColor={"border-[#34a853]"}
      headerVariant="hybrid"
      contentWidth="standard"
      introContent={
        <p>
          Update the farm profile, optional project association, and province so
          fields tied to this farm stay organized across the refreshed grower
          workspace.
        </p>
      }
      headerActions={
        isEditing ? (
          <VersionHistoryControl
            activeSummary={history.activeSummary}
            canViewNewer={history.canViewNewer}
            canViewOlder={history.canViewOlder}
            detailState={history.detailState}
            dialogOpen={history.dialogOpen}
            errorMessage={history.errorMessage}
            onClose={history.closeDialog}
            onOpen={history.openDialog}
            onRestore={history.restoreSelectedVersion}
            onSelectVersion={history.selectVersion}
            onShowCurrent={history.showCurrentVersion}
            onViewNewer={
              history.canViewNewer
                ? () => history.selectAdjacentVersion(-1)
                : undefined
            }
            onViewOlder={
              history.canViewOlder
                ? () => history.selectAdjacentVersion(1)
                : undefined
            }
            resourceLabel="Farm"
            restoreState={history.restoreState}
            summaries={history.summaries}
            summaryState={history.summaryState}
          />
        ) : null
      }
      padding={"px-4 pb-6 pt-5 md:px-6 md:pb-8"}
    >
      <div className="h-full w-full">
        <DraftSaveStatus state={autosave} />
        {recoverableDraft ? <Alert severity="info" action={<>
          <Button onClick={() => { autosave.selectRecovery(recoverableDraft); setFarm(recoverableDraft.value); setRecoverableDraft(null); }}>Restore farm draft</Button>
          <Button onClick={() => { autosave.discard(recoverableDraft); setRecoverableDraft(null); }}>Discard farm draft</Button>
        </>}>Recoverable farm edits are available.<DraftRecoveryChoices draft={recoverableDraft} onChoose={setRecoverableDraft} />{recoverableDraft.versionId !== savedFarm.current_version_id ? " The saved farm changed; compare before saving." : ""}</Alert> : null}
        <HistoryViewingBanner
          onOpenHistory={history.openDialog}
          onRestore={history.restoreSelectedVersion}
          onShowCurrent={history.showCurrentVersion}
          onViewNewer={
            history.canViewNewer
              ? () => history.selectAdjacentVersion(-1)
              : undefined
          }
          onViewOlder={
            history.canViewOlder
              ? () => history.selectAdjacentVersion(1)
              : undefined
          }
          restoreDisabled={history.restoreState === "saving"}
          restoring={history.restoreState === "saving"}
          summary={history.activeSummary}
        />
        <div
          className={`overflow-hidden rounded-[8px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(18,28,45,0.08)] ${
            isViewingHistoricalVersion ? "pointer-events-none opacity-85" : ""
          }`}
        >
          <div className="border-b border-slate-200 bg-[#fcfcfa] px-6 py-5">
            <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
              Farm Setup
            </p>
            <h2 className="mt-2 text-[28px] font-black text-slate-900">
              Keep farm details current
            </h2>
            <p className="mt-3 max-w-[760px] text-sm leading-7 text-slate-600">
              Use this form to maintain the farm name, province, and optional
              project association used throughout the grower workflow.
            </p>
          </div>
          <div className="px-6 py-6 md:px-8">
            <div className="max-w-[640px]">
              <FormTextField
                errorFound={errorFields.name}
                fieldState={displayedFarm?.name}
                onChange={(t) => handleStateChange(t, "name")}
                fieldLabel={"Farm Name"}
                modalTitle={"Farm Name"}
                modalDescription={
                  "A farm or farm operation is used as a way to group fields in a way that is useful to the user.If you have multiple fields you can use farms to logically group them. Use a name that is recognizable to you."
                }
              />
              <FormSelectField
                errorFound={false}
                fieldState={displayedFarm?.project_id}
                fieldLabel={"Project"}
                modalTitle={"Project"}
                helperText={"Optional farm label. Join projects from the Projects page to grant reporting access."}
                onChange={(e) => handleStateChange(e.target.value, "project_id")}
                valuesArray={projectValuesArray}
                modalDescription={
                  "Optionally connect this farm to a project. You can leave this empty and assign a project later."
                }
              />
              <FormSelectField
                errorFound={errorFields.province}
                valuesArray={referenceData.provinces || []}
                fieldLabel={"Province Name"}
                fieldState={displayedFarm?.province}
                helperText={"Please select your province"}
                onChange={(e) => handleStateChange(e.target.value, "province")}
                modalTitle={"Province"}
                modalDescription={
                  "Please Select the province where you have the majority of your business operations."
                }
              />

              <div className="mt-4 flex w-full flex-col gap-4 min-[550px]:flex-row">
                <MainButton
                  text={isEditing ? "Save Changes" : "Save and Add Field"}
                  onClick={handleSaveFarm}
                  disabled={isSaveDisabled}
                />
                <MainButton
                  secondary={isEditing}
                  text={"Cancel"}
                  onClick={() =>
                    navigate(isEditing ? `/farm/${farm.id}` : "/dashboard")
                  }
                />
              </div>
              {saveError ? (
                <p className="mt-4 text-sm font-semibold text-[#c62828]">
                  {saveError}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <WarningMessageModal
        title={"Farm Name Already Exists"}
        description={`The Farm name ${farm.name.trim()} already exists in your profile. Please try a new name or modify the name of the existing Farm by opening it and navigating to the editing controls.`}
        open={warningOpen}
        handleClose={setWarningOpen}
      />
    </Page>
  );
}
